import type { IOffer } from '@bridebook/models/source/models/Suppliers/Offers.types';
import { getSupplierUrl } from '@bridebook/toolbox';
import { SentryMinimal } from '@bridebook/toolbox/src/sentry';
import { AI_SEARCH_VERSION } from '@bridebook/web/lib/ai-assistant/config';
import type { AISearchFiltersSourceOrigin } from '@bridebook/web/lib/ai-assistant/types';
import { createWeddingLevelIdentify, variationTest } from '@bridebook/web/lib/analytics-utils';
import { getIsCollaborator } from '@bridebook/web/lib/analytics-utils/get-is-collaborator';
import { getScreenType } from '@bridebook/web/lib/app/selectors';
import { WebAnalyticsContext } from '@bridebook/web/lib/bbcommon/utils/bridebook-analytics';
import { env } from '@bridebook/web/lib/env';
import type { Action, IApplicationState, IDeps, IElasticSupplier } from '@bridebook/web/lib/types';
import { IMAGES_BASE_URL } from '@bridebook/toolbox/src/buildImageUrl';
import { weddingDetailsPropertiesGeneric } from '@bridebook/web/lib/weddings/analytics';
import type {
  CarouselPropArg,
  NavigationDestination,
  NavigationLocation,
  NavPropArg,
  TilesCarouselAction,
  TilesCarouselLocation,
} from '../analyticsTypes';
import { OfferTypes } from '../offers/constants';
import { convertLateDatesToTimestamps } from '../offers/utils';
import type { TSectionName } from '../wedding-website/types';
import { UIActionTypes } from './action-types';
import type { ISpecialOffersPayload } from './types';

function getNavigationMethod(
  getState: () => IApplicationState,
  destination: NavigationDestination,
) {
  const state = getState();

  const { isMobile } = getScreenType(state);

  switch (destination) {
    case 'home':
      return isMobile ? 'mobileHomeTab' : 'desktopHomeTab';
    case 'messages':
      return isMobile ? 'mobileMessage' : 'desktopMessage';
    case 'planning':
      return isMobile ? 'mobilePlanningTab' : 'desktopPlanningTab';
    case 'inspiration':
      return isMobile ? 'mobileInspirationTab' : 'desktopInspirationTab`';
    case 'suppliers':
      return isMobile ? 'mobileSupplierTab' : 'desktopSupplierTab';
    case 'shortlist':
      return isMobile ? 'mobileFavouritesTab' : 'desktopFavouritesTab';
  }

  return isMobile ? 'mobileHeaderFull' : 'desktopHeaderFull';
}

const navigationPropertiesGeneric = ({ method, destination, location }: NavPropArg) => ({
  navigationDestination: destination,
  navigationMethod: method,
  ...(location && { navigationLocation: location }),
});

export const tilesCarouselPropertiesGeneric = ({ action, location }: CarouselPropArg) => ({
  tilesCarouselAction: action,
  tilesCarouselLocation: location,
});

export function getUsedNavFunction(type: string, event: string) {
  return (destination: NavigationDestination, location?: NavigationLocation) =>
    ({ getState }: IDeps) => {
      const method = getNavigationMethod(getState, destination);
      return {
        type: `${type}_ANALYTICS`,
        payload: { event, method, destination, location },
      };
    };
}

export function getTilesCarouselFunction(type: string, event: string) {
  return (action: TilesCarouselAction, location: TilesCarouselLocation = 'home') => ({
    type: `${type}_ANALYTICS`,
    payload: { event, action, location },
  });
}

export interface IClickedAnalyticsPayload {
  category?: string;
  cta?: string;
  clickedSection?: string;
  clickedLocation?: string;
  mobileApp?: boolean;
  supplierId?: string;
  sectionsOrderDict?: Record<number, TSectionName>;
  defaultOrderingFlag?: boolean;
  themeGeneratedAi?: boolean;
}

export const clickedAnalytics =
  <T extends IClickedAnalyticsPayload>(payload: T) =>
  ({ getState }: IDeps) => {
    const state = getState();
    return {
      type: UIActionTypes.CLICKED,
      payload: {
        previousPath: state.app.previousPath,
        ...payload,
      },
    };
  };

/** `category`, `feature` and `version` are constants for this event and are injected by the handler. */
export interface IInteractedWithAIToolPayload {
  action: 'clicked' | 'sent' | 'viewed';
  location: 'search' | 'searchAi';
  section: string;
  element: string;
  elementLabel?: string;
}

export const interactedWithAIToolAnalytics =
  (payload: IInteractedWithAIToolPayload) =>
  ({ getState }: IDeps) => {
    const state = getState();
    return {
      type: UIActionTypes.INTERACTED_WITH_AI_TOOL,
      payload: {
        previousPath: state.app.previousPath,
        ...payload,
      },
    };
  };

/**
 * `AI Prompt Sent` is the BigQuery-only event for every prompt submission.
 * Routing to BigQuery (skipping Mixpanel) is gated on the `trackingTarget`
 * property — set on the event by the handler. All generic user-payload
 * properties (userId, weddingId, countryCode, mobileApp, sessionId, locale,
 * etc.) are auto-injected by the analytics middleware's `commonProps`,
 * exactly as for any other event.
 */
export interface IAIPromptSentPayload {
  version: string;
  parentPromptId: string | null;
  childPromptId: string | null;
  promptContent: string;
  promptSource: 'recommended' | 'typed';
  recommendedPromptTemplate?: string;
  recommendedPromptOrder?: number;
  recommendedPromptOptions?: string[];
  searchActiveFiltersSource?: Record<string, AISearchFiltersSourceOrigin>;
  /** Null on conversational/non-search turns (no search ran). */
  searchActiveFilters: Record<string, Record<string, string | boolean>> | null;
  carouselFlag: boolean;
  carouselLoadedList: string[];
  /** Null on conversational/non-search turns (no search ran). */
  resultsCount: number | null;
  /**
   * Omitted when no advisory message was shown on this turn. The two failure
   * statuses are emitted from the Provider's `onError` with null search fields so
   * BQ still receives the attempt: `contextLimitReached` (over-length gate
   * rejected the turn even after trimming) and `rateLimitReached` (429).
   */
  resultsMessageStatus?:
    | 'zeroResults'
    | 'supplierSearch'
    | 'narrowYourSearch'
    | 'widenYourSearch'
    | 'contextLimitReached'
    | 'rateLimitReached';
  searchAiResponseContent: string;
}

export const aiPromptSentAnalytics =
  (payload: IAIPromptSentPayload) =>
  ({ getState }: IDeps) => {
    const state = getState();
    return {
      type: UIActionTypes.AI_PROMPT_SENT,
      payload: {
        previousPath: state.app.previousPath,
        ...payload,
      },
    };
  };

export const specialOfferPropertiesGeneric = ({ type, offer }: ISpecialOffersPayload) => {
  const { title, details, expiration } = offer || ({} as IOffer);
  const specialOfferDates =
    type === OfferTypes.lateAvailability && offer?.dates
      ? convertLateDatesToTimestamps(offer?.dates)
      : undefined;

  return {
    specialOfferType: type,
    specialOfferTitle: title,
    specialOfferDetails: details,
    specialOfferDeadline: expiration,
    specialOfferDates,
  };
};

export default function uiAnalytics(
  { type, payload }: Action,
  bridebookAnalytics: WebAnalyticsContext,
  getState: () => IApplicationState,
) {
  const { track } = bridebookAnalytics.getMethods('Navigation');

  switch (type) {
    case 'USED_HEADER_NAVIGATION_ANALYTICS':
    case 'USED_MENU_NAVIGATION_ANALYTICS':
    case 'USED_TOOL_CARDS_ANALYTICS':
    case 'USED_HEADER_HOME_BUTTON_ANALYTICS': {
      const { event, method, destination, location } = payload;
      const state = getState();
      const collaborator = getIsCollaborator(state);
      const previousPath = state.app.previousPath;
      const isMenu = type === 'USED_MENU_NAVIGATION_ANALYTICS';
      const userEmail = state.users.user?.contacts?.email;
      const navigationDestination =
        isMenu && destination === 'booked suppliers' ? 'booked' : undefined;
      // find first non-generic value in path to determine current user's general location
      const navigationSource = state.app.lastPath.split('/').find((s) => s && !s.includes('['));

      track(
        {
          event,
          profileType: 'user',
          collaborator,
          previousPath,
          userEmail,
          ...navigationPropertiesGeneric({ method, destination, location }),
          ...(navigationDestination &&
            type !== 'USED_TOOL_CARDS_ANALYTICS' && { navigationDestination }),
          ...(navigationSource &&
            type === 'USED_HEADER_NAVIGATION_ANALYTICS' && { navigationSource }),
        },
        payload?.callback,
      );
      break;
    }
    case 'USED_RECENTLY_VIEWED_TILES_CAROUSEL_ANALYTICS':
    case 'USED_ARTICLES_TILES_CAROUSEL_ANALYTICS': {
      const { event, action, location } = payload;
      track({
        event,
        ...tilesCarouselPropertiesGeneric({ action, location }),
      });
      break;
    }
    // https://docs.google.com/spreadsheets/d/1sXMNsU7ThA7Yjwgfxg-JqXxGeziRneNnjLyxrdbvV_c/edit#gid=346801395
    // Miscellaneous tracking events v28 2017 06 15
    case 'USER_CLICKED_OUTBOUND_LINK_ANALYTICS': {
      const { url, type } = payload;
      const href = variationTest(getState);
      track({
        event: 'User clicked outbound link',
        category: 'Outbound links',
        clickedOutboundLinkType: type,
        clickedOutboundLinkURL: url,
        url: href,
      });
      break;
    }
    case 'USED_COUNTRY_MAP_ON_DIRECTORY_LANDING_PAGE_ANALYTICS': {
      const { country } = payload;
      track({
        event: 'Used country map on directory landing page',
        category: 'Landing Page',
        usedCountryMapClickedCountry: country,
      });
      break;
    }
    case 'CLICKED_TILE_ON_TOOLS_LANDING_PAGE_ANALYTICS': {
      const { clickedTileType } = payload;
      track({
        event: 'Clicked tile on tools landing page',
        category: 'Landing pages',
        clickedTileType,
      });
      break;
    }

    case UIActionTypes.TRIGGERED_SPECIAL_OFFER_POP_UP_ANALYTICS:
      track({
        event: 'Triggered special offer pop up',
        ...specialOfferPropertiesGeneric(payload as ISpecialOffersPayload),
      });
      break;

    case UIActionTypes.COUPLE_CLICKED_INBOX_MENU_ANALYTICS: {
      const state = getState();
      const userEmail = state.users.user?.contacts?.email;
      track({
        event: 'Couple clicked Messages item in burger menu',
        userEmail,
        ...weddingDetailsPropertiesGeneric(state),
      });
      break;
    }

    case UIActionTypes.IDENTIFY_VENUE_RECOMMENDED_SUPPLIERS:
      {
        const state = getState();
        const { venueRecommendedSuppliers, name } = payload;
        const { identifyWithTrack: identify } = bridebookAnalytics.getMethods('All');
        const eventName =
          name === 'recommendedLocalSuppliers'
            ? 'Recommended local suppliers'
            : 'Venue recommended suppliers';
        const mappedList = venueRecommendedSuppliers.map((supplier: IElasticSupplier) => ({
          supplierId: supplier.id,
          country: supplier.country,
          county: supplier.county,
          description: supplier.description,
          name: supplier.name,
          thumbnail: `${IMAGES_BASE_URL}/${supplier.thumbnail}`,
          minPrice: supplier.price || supplier.supplierPriceMin,
          category: supplier.type,
          supplierURL: env.COUPLESIDE_URL + getSupplierUrl(supplier),
        }));
        try {
          identify(
            {
              isAnonymous: false,
              ...(name ? { [name]: mappedList } : { venueRecommendedSuppliers: mappedList }),
            },
            {
              event: eventName,
              ...(name ? { [name]: mappedList } : { venueRecommendedSuppliers: mappedList }),
            },
            { userId: state.users.user?.id as string },
          );
        } catch (e) {
          SentryMinimal().captureException(e, {
            tags: {
              source: name || 'venueRecommendedSuppliers',
            },
          });
        }
        const serverIdentify = createWeddingLevelIdentify<{ pbStatus: boolean }>(getState);
        serverIdentify({ pbStatus: true });
      }

      break;
    case UIActionTypes.CLICKED: {
      const state = getState();
      const collaborator = getIsCollaborator(state);
      track({
        event: 'Clicked',
        ...payload,
        collaborator,
      });
      break;
    }

    case UIActionTypes.INTERACTED_WITH_AI_TOOL: {
      track({
        event: 'Interacted with AI tool',
        category: 'aITool',
        feature: 'searchAi',
        version: AI_SEARCH_VERSION,
        ...payload,
      });
      break;
    }

    case UIActionTypes.AI_PROMPT_SENT: {
      // `trackingTarget` is the property the data team's MetaRouter
      // destination filter reads to route this event to BigQuery only.
      track({
        event: 'AI Prompt Sent',
        trackingTarget: 'BigQuery',
        ...payload,
      });
      break;
    }

    // eslint-disable-next-line no-fallthrough
    default:
      break;
  }
}
